import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Alert, Box, Button, Stack } from '@mui/material';
import { ApiError } from '@/api';
import { useAuth } from '@/auth';
import { SplashScreen } from '@/components/SplashScreen';
import { AccessBlockedPage } from './AccessBlockedPage';
import { getMe } from './api';
import { CompleteProfilePage } from './CompleteProfilePage';
import {
  ALL_INSTITUTIONS,
  chooseInstitution,
  rememberTestProfile,
  storedInstitution,
  testProfileChosen,
} from './institutionChoice';
import { InstitutionPickerPage } from './InstitutionPickerPage';
import { PendingApprovalPage } from './PendingApprovalPage';
import { SessionContext, type SessionValue } from './session';
import { TestProfilePage } from './TestProfilePage';
import type { Me } from './types';
import { ViewAsBanner } from './ViewAsBanner';
import { stopViewAs, viewingAs } from './viewAs';

const STAFF = new Set(['admin', 'coordinator', 'teacher']);

/**
 * The institution a staff member looks at: the one they picked (remembered until sign-out),
 * the only one they have, or null when they still have to pick. Students: their own.
 */
function resolveInstitution(me: Me, picked: string | null): string | null {
  const isAdmin = me.user.role === 'admin';
  if (!STAFF.has(me.user.role)) return me.user.institutions[0]?.id ?? null;
  const wanted = picked ?? storedInstitution(me.user.id);
  if (wanted === ALL_INSTITUTIONS && isAdmin) return ALL_INSTITUTIONS;
  if (wanted && me.institutions.some((i) => i.id === wanted)) return wanted;
  if (!isAdmin && me.institutions.length === 1) return me.institutions[0].id;
  if (isAdmin && me.institutions.length === 0) return ALL_INSTITUTIONS;
  return null;
}

/**
 * Loads `/me` once the user is signed in: role, profile and the modules to show.
 * Everything below it can call `useSession()`.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [picked, setPicked] = useState<string | null>(null);
  // Test accounts choose student/teacher once per sign-in (null: not looked up yet).
  const [testChosen, setTestChosen] = useState<boolean | null>(null);
  const userId = user?.id;

  const load = useCallback(async () => {
    try {
      setMe(await getMe());
      setError(null);
    } catch (err) {
      // 403 is final (e.g. the account was deactivated while waiting): show it.
      if (err instanceof ApiError && err.status === 403) setMe(null);
      setError(err);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    getMe(controller.signal).then(
      (data) => {
        setMe(data);
        setError(null);
      },
      (err: unknown) => {
        if (!controller.signal.aborted) setError(err);
      },
    );
    return () => controller.abort();
  }, [userId]);

  const institutionId = me ? resolveInstitution(me, picked) : null;
  const staff = Boolean(me && STAFF.has(me.user.role));
  // Before the pages below fetch anything (their effects run after this one).
  useLayoutEffect(() => {
    if (me) chooseInstitution(me.user.id, staff ? institutionId : null);
  }, [me, staff, institutionId]);

  const value = useMemo<SessionValue | null>(() => {
    if (!me) return null;
    const enabled = me.modules.filter((m) => m.enabled);
    const role = me.user.role;
    const options = STAFF.has(role) ? me.institutions : me.user.institutions;
    return {
      me,
      user: me.user,
      role,
      isAdmin: role === 'admin',
      isManager: role === 'admin' || role === 'coordinator',
      isStaff: STAFF.has(role),
      modules: enabled,
      allModules: me.modules,
      hasModule: (key) => enabled.some((m) => m.key === key),
      institutions: options,
      institution: options.find((i) => i.id === institutionId) ?? null,
      setInstitution: setPicked,
      testAccount: me.testAccount,
      changeTestProfile: () => {
        rememberTestProfile(me.user.id, false);
        setTestChosen(false);
      },
      refresh: () => load(),
    };
  }, [me, load, institutionId]);
  const askTestProfile =
    Boolean(me?.testAccount) && !(testChosen ?? (me ? testProfileChosen(me.user.id) : false));

  if (error && !me) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 3 }}>
        <Stack spacing={2} sx={{ maxWidth: 420 }}>
          <Alert severity="error">
            {error instanceof ApiError && error.status === 403
              ? error.message // e.g. account deactivated (a refused teacher request)
              : 'Não foi possível carregar sua conta. Verifique se a API está no ar e tente de novo.'}
          </Alert>
          <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
            <Button color="inherit" onClick={() => void signOut()}>
              Sair
            </Button>
            {viewingAs() && (
              // E.g. the student was removed while being looked at.
              <Button color="inherit" onClick={stopViewAs}>
                Voltar para a minha conta
              </Button>
            )}
            <Button variant="contained" onClick={() => void load()}>
              Tentar de novo
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }
  if (!value) return <SplashScreen />;
  return (
    <SessionContext.Provider value={value}>
      <ViewAsBanner me={value.me} />
      {askTestProfile ? (
        <TestProfilePage
          onChosen={() => {
            rememberTestProfile(value.user.id, true);
            setTestChosen(true);
            setPicked(null);
          }}
        />
      ) : !value.me.profileComplete ? (
        <CompleteProfilePage />
      ) : value.me.approvalPending ? (
        <PendingApprovalPage />
      ) : value.me.accessBlocked ? (
        <AccessBlockedPage />
      ) : staff && !institutionId ? (
        <InstitutionPickerPage />
      ) : (
        // A different institution is a different data set: remount so every page reloads.
        <Fragment key={institutionId ?? ''}>{children}</Fragment>
      )}
    </SessionContext.Provider>
  );
}
